import { Room, RoomEvent, Track } from 'livekit-client';

export type BrowserTimingEvent =
  | 'official_WebRTC_connected'
  | 'LiveKit_signaling_started'
  | 'LiveKit_connected'
  | 'official_signaling_started'
  | 'browser_audio_output_prewarmed'
  | 'remote_audio_track_available'
  | 'audio_element_playing'
  | 'official_tracks_attached'
  | 'candidate_audio_progress'
  | 'browser_media_quality_sample'
  | 'official_video_track_muted'
  | 'official_video_track_resumed'
  | 'official_video_track_ended'
  | 'official_video_stalled'
  | 'first_stable_visible_frame'
  | 'first_audible_browser_audio'
  | 'first_visible_browser_response'
  | 'candidate_browser_audio_started'
  | 'candidate_browser_audio_ended'
  | 'candidate_browser_audio_packet_published'
  | 'browser_audio_RTP_packet_received'
  | 'browser_audio_jitter_buffer_emitted'
  | 'browser_audio_decoder_energy_started'
  | 'browser_video_RTP_packet_received'
  | 'browser_clock_sync';

export type OfficialAvatarMediaSession = {
  roomName: string;
  liveKitUrl: string;
  accessToken: string;
};

export type AvatarOffer = { sdp: string; type: 'answer' };

type MediaCallbacks = {
  iceServers: (signal?: AbortSignal) => Promise<RTCConfiguration>;
  offer: (value: RTCSessionDescriptionInit, signal?: AbortSignal) => Promise<AvatarOffer>;
  report: (
    type: BrowserTimingEvent,
    metadata?: Record<string, unknown>
  ) => Promise<Record<string, unknown>>;
};

type AudioContextConstructor = new (contextOptions?: AudioContextOptions) => AudioContext;

export type InterviewAudioOutputWarmup = {
  context: AudioContext | null;
  result: Promise<{
    available: boolean;
    elementPrimed: boolean;
    running: boolean;
    startedAtMonotonicMs: number;
    readyAtMonotonicMs: number;
  }>;
  close: () => Promise<void>;
};

export type OfficialSignalingMilestone =
  | 'signaling_started'
  | 'ice_configuration_received'
  | 'peer_connection_created'
  | 'offer_created'
  | 'local_description_set'
  | 'first_ice_candidate'
  | 'ice_gathering_state'
  | 'ice_bounded_cutoff'
  | 'offer_submitted'
  | 'answer_received'
  | 'remote_description_set'
  | 'cleanup';

export type OfficialSignalingMilestoneEvent = {
  type: OfficialSignalingMilestone;
  monotonicMs: number;
  metadata?: Record<string, unknown>;
};

export type IceGatheringResult = {
  outcome: 'complete' | 'candidate-ready' | 'bounded-cutoff';
  durationMs: number;
  candidateTypes: string[];
};

export const OFFICIAL_ICE_CONFIGURATION_TIMEOUT_MS = 2_500;
export const OFFICIAL_ICE_GATHERING_CUTOFF_MS = 5_000;
// Chrome otherwise selected roughly 860 ms of jitter-buffer target on the
// owned-avatar relay path despite only 22-24 ms of measured network jitter.
// Apply the same conservative target to the synchronized audio and video
// receivers so audio is not held behind a much larger browser-selected AV
// playout buffer. The user agent remains authoritative and may clamp it.
export const OFFICIAL_AVATAR_JITTER_BUFFER_TARGET_MS = 200;
export const OFFICIAL_AVATAR_JITTER_BUFFER_TEST_TARGETS_MS = [200, 150, 100] as const;
const OFFICIAL_ICE_CANDIDATE_SETTLE_MS = 75;

type RtpReceiverWithJitterTarget = RTCRtpReceiver & {
  jitterBufferTarget?: number | null;
};

const resolveOfficialAvatarJitterBufferTargetMs = () => {
  const requested = Number(
    new URLSearchParams(window.location.search).get('ownedAvatarJitterBufferTargetMs')
  );
  return OFFICIAL_AVATAR_JITTER_BUFFER_TEST_TARGETS_MS.includes(
    requested as (typeof OFFICIAL_AVATAR_JITTER_BUFFER_TEST_TARGETS_MS)[number]
  )
    ? requested
    : OFFICIAL_AVATAR_JITTER_BUFFER_TARGET_MS;
};

const applyOfficialAvatarJitterBufferTarget = (
  pc: RTCPeerConnection,
  targetMs = OFFICIAL_AVATAR_JITTER_BUFFER_TARGET_MS
) => {
  const applied: Record<string, number | null> = {};
  if (typeof pc.getReceivers !== 'function') return applied;
  for (const receiver of pc.getReceivers()) {
    if (receiver.track.kind !== 'audio' && receiver.track.kind !== 'video') continue;
    const configurable = receiver as RtpReceiverWithJitterTarget;
    if (!('jitterBufferTarget' in configurable)) {
      applied[receiver.track.kind] = null;
      continue;
    }
    try {
      configurable.jitterBufferTarget = targetMs;
      applied[receiver.track.kind] = configurable.jitterBufferTarget ?? null;
    } catch {
      applied[receiver.track.kind] = null;
    }
  }
  return applied;
};

const browserAudioContextConstructor = (): AudioContextConstructor | undefined =>
  window.AudioContext ||
  (window as typeof window & { webkitAudioContext?: AudioContextConstructor })
    .webkitAudioContext;

export const prewarmInterviewAudioOutput = (
  audioElement: HTMLAudioElement,
  AudioContextClass = browserAudioContextConstructor()
): InterviewAudioOutputWarmup => {
  const startedAtMonotonicMs = Math.round(performance.now());
  audioElement.autoplay = true;
  audioElement.preload = 'auto';
  let elementPrimed = false;
  try {
    // Chromium uses load()/play() from a user gesture to unlock this specific
    // media element. With no srcObject or URL this cannot emit fake content.
    audioElement.load();
    elementPrimed = true;
  } catch {
    // Keep the warm-up best-effort; real-stream play() remains fail-closed.
  }
  if (!AudioContextClass) {
    return {
      context: null,
      result: Promise.resolve({
        available: false,
        elementPrimed,
        running: false,
        startedAtMonotonicMs,
        readyAtMonotonicMs: Math.round(performance.now()),
      }),
      close: async () => undefined,
    };
  }

  let context: AudioContext | null = null;
  try {
    // This runs synchronously inside the Start-button gesture. No source,
    // buffer, oscillator, destination connection, or fabricated media is
    // created; it only opens the browser's standards-based audio output path.
    context = new AudioContextClass({ latencyHint: 'interactive' });
  } catch {
    return {
      context: null,
      result: Promise.resolve({
        available: false,
        elementPrimed,
        running: false,
        startedAtMonotonicMs,
        readyAtMonotonicMs: Math.round(performance.now()),
      }),
      close: async () => undefined,
    };
  }
  const warmedContext = context;
  const result = warmedContext
    .resume()
    .then(() => ({
      available: true,
      elementPrimed,
      running: warmedContext.state === 'running',
      startedAtMonotonicMs,
      readyAtMonotonicMs: Math.round(performance.now()),
    }))
    .catch(() => ({
      available: true,
      elementPrimed,
      running: false,
      startedAtMonotonicMs,
      readyAtMonotonicMs: Math.round(performance.now()),
    }));
  let closed = false;
  return {
    context: warmedContext,
    result,
    close: async () => {
      if (closed) return;
      closed = true;
      if (warmedContext.state !== 'closed') {
        await warmedContext.close().catch(() => undefined);
      }
    },
  };
};

const abortError = () =>
  new DOMException('Official interviewer connection was cancelled.', 'AbortError');

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) throw abortError();
};

const delayWithAbort = (durationMs: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    throwIfAborted(signal);
    const timeout = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, durationMs);
    const onAbort = () => {
      window.clearTimeout(timeout);
      signal?.removeEventListener('abort', onAbort);
      reject(abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });

const iceUrls = (configuration: RTCConfiguration) =>
  (configuration.iceServers || []).flatMap((server) =>
    typeof server.urls === 'string' ? [server.urls] : Array.from(server.urls || [])
  );

export const validateOfficialIceConfiguration = (configuration: RTCConfiguration) => {
  const urls = iceUrls(configuration);
  const hasTurn = urls.some((url) => /^turns?:/i.test(url));
  if (configuration.iceTransportPolicy === 'relay' && !hasTurn) {
    throw new Error('official_avatar_ice_configuration_requires_turn');
  }
  return {
    hasStun: urls.some((url) => /^stun:/i.test(url)),
    hasTurn,
  };
};

const candidateType = (candidate: RTCIceCandidate) => {
  if (candidate.type) return candidate.type;
  return /\btyp\s+(host|srflx|prflx|relay)\b/i.exec(candidate.candidate)?.[1] || 'unknown';
};

const sdpCandidateTypes = (sdp: string) =>
  [...sdp.matchAll(/\ba=candidate:[^\r\n]*\btyp\s+(host|srflx|prflx|relay)\b/gi)]
    .map((match) => match[1].toLowerCase());

export const waitForBoundedIceGathering = (
  pc: RTCPeerConnection,
  options: {
    configuration: RTCConfiguration;
    signal?: AbortSignal;
    timeoutMs?: number;
    settleMs?: number;
    onMilestone?: (event: OfficialSignalingMilestoneEvent) => void;
  }
) => {
  const {
    configuration,
    signal,
    timeoutMs = OFFICIAL_ICE_GATHERING_CUTOFF_MS,
    settleMs = OFFICIAL_ICE_CANDIDATE_SETTLE_MS,
    onMilestone,
  } = options;
  const startedAt = performance.now();
  const capabilities = validateOfficialIceConfiguration(configuration);
  const desiredType = capabilities.hasTurn
    ? 'relay'
    : capabilities.hasStun
      ? 'srflx'
      : 'host';
  const observedTypes = new Set<string>(
    sdpCandidateTypes(pc.localDescription?.sdp || '')
  );

  return new Promise<IceGatheringResult>((resolve, reject) => {
    throwIfAborted(signal);
    let finished = false;
    let cutoff = 0;
    let settle = 0;
    let firstCandidateReported = false;
    const refreshSerializedCandidateTypes = () => {
      for (const type of sdpCandidateTypes(pc.localDescription?.sdp || '')) {
        observedTypes.add(type);
      }
    };
    const finish = (outcome: IceGatheringResult['outcome']) => {
      if (finished) return;
      refreshSerializedCandidateTypes();
      finished = true;
      window.clearTimeout(cutoff);
      window.clearTimeout(settle);
      pc.removeEventListener('icecandidate', onCandidate);
      pc.removeEventListener('icegatheringstatechange', onGatheringState);
      signal?.removeEventListener('abort', onAbort);
      resolve({
        outcome,
        durationMs: Math.round((performance.now() - startedAt) * 100) / 100,
        candidateTypes: [...observedTypes].sort(),
      });
    };
    const finishWhenDesiredCandidateIsSerialized = () => {
      if (finished) return;
      refreshSerializedCandidateTypes();
      if (sdpCandidateTypes(pc.localDescription?.sdp || '').includes(desiredType)) {
        finish('candidate-ready');
        return;
      }
      // Chromium can emit the candidate event just before it updates
      // pc.localDescription. Keep the existing bounded deadline, but never
      // submit an SDP offer that does not yet contain the candidate we saw.
      settle = window.setTimeout(
        finishWhenDesiredCandidateIsSerialized,
        Math.min(settleMs, 25)
      );
    };
    const onAbort = () => {
      if (finished) return;
      finished = true;
      window.clearTimeout(cutoff);
      window.clearTimeout(settle);
      pc.removeEventListener('icecandidate', onCandidate);
      pc.removeEventListener('icegatheringstatechange', onGatheringState);
      signal?.removeEventListener('abort', onAbort);
      reject(abortError());
    };
    const onCandidate = (event: RTCPeerConnectionIceEvent) => {
      if (!event.candidate) return;
      const type = candidateType(event.candidate);
      observedTypes.add(type);
      if (!firstCandidateReported) {
        firstCandidateReported = true;
        onMilestone?.({
          type: 'first_ice_candidate',
          monotonicMs: performance.now(),
          metadata: { candidateType: type },
        });
      }
      if (type === desiredType && !settle) {
        settle = window.setTimeout(finishWhenDesiredCandidateIsSerialized, settleMs);
      }
    };
    const onGatheringState = () => {
      onMilestone?.({
        type: 'ice_gathering_state',
        monotonicMs: performance.now(),
        metadata: { state: pc.iceGatheringState },
      });
      if (pc.iceGatheringState === 'complete') finish('complete');
    };
    pc.addEventListener('icecandidate', onCandidate);
    pc.addEventListener('icegatheringstatechange', onGatheringState);
    signal?.addEventListener('abort', onAbort, { once: true });
    if (pc.iceGatheringState === 'complete') {
      finish('complete');
      return;
    }
    if (observedTypes.has(desiredType)) {
      settle = window.setTimeout(finishWhenDesiredCandidateIsSerialized, settleMs);
    }
    cutoff = window.setTimeout(() => {
      onMilestone?.({
        type: 'ice_bounded_cutoff',
        monotonicMs: performance.now(),
        metadata: { candidateTypes: [...observedTypes].sort() },
      });
      finish('bounded-cutoff');
    }, timeoutMs);
  });
};

const loadOfficialIceConfiguration = async (
  load: (signal?: AbortSignal) => Promise<RTCConfiguration>,
  signal?: AbortSignal,
  timeoutMs = OFFICIAL_ICE_CONFIGURATION_TIMEOUT_MS
) => {
  throwIfAborted(signal);
  const controller = new AbortController();
  let timedOut = false;
  const relayAbort = () => controller.abort();
  signal?.addEventListener('abort', relayAbort, { once: true });
  const timeout = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    const aborted = new Promise<never>((_resolve, reject) => {
      controller.signal.addEventListener('abort', () => reject(abortError()), {
        once: true,
      });
    });
    const configuration = await Promise.race([load(controller.signal), aborted]);
    throwIfAborted(signal);
    validateOfficialIceConfiguration(configuration);
    return configuration;
  } catch (error) {
    if (timedOut) throw new Error('official_avatar_ice_configuration_timeout');
    throwIfAborted(signal);
    throw error;
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener('abort', relayAbort);
  }
};

export const negotiateOfficialAvatarPeer = async (args: {
  microphoneTrack: MediaStreamTrack;
  microphoneStream?: MediaStream;
  iceServers: (signal?: AbortSignal) => Promise<RTCConfiguration>;
  offer: (value: RTCSessionDescriptionInit, signal?: AbortSignal) => Promise<AvatarOffer>;
  signal?: AbortSignal;
  isCurrent?: () => boolean;
  onPeerConnection?: (pc: RTCPeerConnection) => void;
  onMilestone?: (event: OfficialSignalingMilestoneEvent) => void;
  jitterBufferTargetMs?: number;
}) => {
  const startedAt = performance.now();
  const milestone = (
    type: OfficialSignalingMilestone,
    metadata?: Record<string, unknown>
  ) => args.onMilestone?.({ type, monotonicMs: performance.now(), metadata });
  milestone('signaling_started');
  const configuration = await loadOfficialIceConfiguration(args.iceServers, args.signal);
  milestone('ice_configuration_received', {
    iceServerCount: configuration.iceServers?.length || 0,
    iceTransportPolicy: configuration.iceTransportPolicy || 'all',
  });
  throwIfAborted(args.signal);
  if (args.isCurrent && !args.isCurrent()) {
    throw new Error('official_avatar_stale_session');
  }

  const pc = new RTCPeerConnection(configuration);
  let offerSubmitted = false;
  const closeOnAbort = () => pc.close();
  args.signal?.addEventListener('abort', closeOnAbort, { once: true });
  try {
    milestone('peer_connection_created');
    const microphoneStream = args.microphoneStream || new MediaStream([args.microphoneTrack]);
    pc.addTrack(args.microphoneTrack, microphoneStream);
    const videoTransceiver = pc.addTransceiver('video', { direction: 'recvonly' });
    preferHighQualityOfficialVideoDelivery(videoTransceiver);
    // Both receivers now exist. Configure their shared target before offer /
    // ICE so Chrome can begin converging during signaling and first-question
    // rendering instead of waiting for the later remote-track callbacks.
    applyOfficialAvatarJitterBufferTarget(
      pc,
      args.jitterBufferTargetMs ?? OFFICIAL_AVATAR_JITTER_BUFFER_TARGET_MS
    );
    args.onPeerConnection?.(pc);
    const offer = await pc.createOffer();
    throwIfAborted(args.signal);
    milestone('offer_created');
    await pc.setLocalDescription(offer);
    throwIfAborted(args.signal);
    milestone('local_description_set');
    const gathering = await waitForBoundedIceGathering(pc, {
      configuration,
      signal: args.signal,
      onMilestone: args.onMilestone,
    });
    const localDescription = pc.localDescription;
    if (!localDescription) {
      throw new Error('official_avatar_local_description_missing');
    }
    const gatheredTypes = sdpCandidateTypes(localDescription.sdp || '');
    if (gatheredTypes.length === 0) {
      throw new Error('official_avatar_ice_gathering_no_candidates');
    }
    if (
      validateOfficialIceConfiguration(configuration).hasTurn &&
      !gatheredTypes.includes('relay')
    ) {
      throw new Error('official_avatar_ice_gathering_relay_missing');
    }
    throwIfAborted(args.signal);
    if (args.isCurrent && !args.isCurrent()) {
      throw new Error('official_avatar_stale_session');
    }
    if (offerSubmitted) throw new Error('official_avatar_duplicate_offer');
    offerSubmitted = true;
    milestone('offer_submitted', {
      candidateCount: gatheredTypes.length,
      candidateTypes: [...new Set(gatheredTypes)].sort(),
      gatheringOutcome: gathering.outcome,
      signalingToOfferMs: Math.round((performance.now() - startedAt) * 100) / 100,
    });
    const answer = await args.offer(localDescription, args.signal);
    throwIfAborted(args.signal);
    if (args.isCurrent && !args.isCurrent()) {
      throw new Error('official_avatar_stale_session');
    }
    milestone('answer_received');
    await pc.setRemoteDescription(answer);
    throwIfAborted(args.signal);
    milestone('remote_description_set');
    return { pc, gathering };
  } catch (error) {
    pc.close();
    throw error;
  } finally {
    args.signal?.removeEventListener('abort', closeOnAbort);
  }
};

export const preferHighQualityOfficialVideoDelivery = (
  transceiver: RTCRtpTransceiver | undefined,
  capabilities?: RTCRtpCapabilities | null
) => {
  const available = capabilities === undefined
    ? typeof RTCRtpReceiver === 'undefined'
      ? null
      : RTCRtpReceiver.getCapabilities('video')
    : capabilities;
  if (!transceiver || !available || typeof transceiver.setCodecPreferences !== 'function') {
    return false;
  }
  const vp8 = available.codecs.filter(
    (codec) => codec.mimeType.toLowerCase() === 'video/vp8'
  );
  if (vp8.length === 0) return false;
  const remaining = available.codecs.filter(
    (codec) => codec.mimeType.toLowerCase() !== 'video/vp8'
  );
  // The human review used H.264 at only 0.27-0.57 Mbps for 1280x720. The
  // immediately preceding same-path evidence negotiated VP8 at 0.49-1.52
  // Mbps on the same TURN route. Prefer that demonstrably sharper shared
  // codec while retaining every negotiated fallback.
  transceiver.setCodecPreferences([...vp8, ...remaining]);
  return true;
};

export const acquireInterviewMicrophone = async (
  mediaDevices: Pick<MediaDevices, 'getUserMedia'> = navigator.mediaDevices,
  timeoutMs = 5_000
) => {
  if (!mediaDevices?.getUserMedia) {
    throw new Error('Microphone access is unavailable in this browser.');
  }
  let timedOut = false;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const microphoneRequest = mediaDevices
    .getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    })
    .then((stream) => {
      if (timedOut) {
        stream.getTracks().forEach((track) => track.stop());
        return undefined;
      }
      return stream;
    });
  const timeoutRequest = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      timedOut = true;
      reject(
        new Error(
          `Microphone access did not complete within ${Math.ceil(timeoutMs / 1_000)} seconds.`
        )
      );
    }, timeoutMs);
  });
  let stream: MediaStream | undefined;
  try {
    stream = await Promise.race([microphoneRequest, timeoutRequest]);
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
  if (!stream) {
    throw new Error(
      `Microphone access did not complete within ${Math.ceil(timeoutMs / 1_000)} seconds.`
    );
  }
  const track = stream.getAudioTracks()[0];
  if (!track || track.readyState !== 'live') {
    stream.getTracks().forEach((candidate) => candidate.stop());
    throw new Error('A live microphone track is required.');
  }
  return { stream, track };
};

export const acquireInterviewCamera = async (
  mediaDevices: Pick<MediaDevices, 'getUserMedia'> = navigator.mediaDevices,
  timeoutMs = 30_000
) => {
  let timedOut = false;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const cameraRequest = mediaDevices
    .getUserMedia({
      audio: false,
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 24, max: 30 },
      },
    })
    .then((stream) => {
      if (timedOut) {
        stream.getTracks().forEach((track) => track.stop());
        return undefined;
      }
      return stream;
    });
  const timeoutRequest = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      timedOut = true;
      reject(
        new Error(
          `Camera access did not complete within ${Math.ceil(timeoutMs / 1_000)} seconds.`
        )
      );
    }, timeoutMs);
  });
  let stream: MediaStream | undefined;
  try {
    stream = await Promise.race([cameraRequest, timeoutRequest]);
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
  const track = stream?.getVideoTracks()[0];
  if (!stream || !track || track.readyState !== 'live') {
    stream?.getTracks().forEach((candidate) => candidate.stop());
    throw new Error('A live camera track is required for the private interview recording.');
  }
  return { stream, track };
};

export const fanOutInterviewMicrophone = (track: MediaStreamTrack) => ({
  liveKitTrack: track,
  officialListeningTrack: track.clone(),
});

export const connectMediaTransportsInParallel = async <TLiveKit, TOfficial>(
  connectLiveKit: () => Promise<TLiveKit>,
  connectOfficial: () => Promise<TOfficial>
) => Promise.all([connectLiveKit(), connectOfficial()] as const);

export const officialVideoSampleIsUsable = (sample: {
  connectionState: RTCPeerConnectionState;
  hasAudio: boolean;
  readyState: number;
  width: number;
  height: number;
  keyFramesDecoded: number;
  framesDecoded: number;
  frameTimes: number;
  playbackAdvanceSeconds: number;
}) =>
  sample.connectionState === 'connected' &&
  sample.hasAudio &&
  sample.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
  sample.width === 1280 &&
  sample.height === 720 &&
  sample.keyFramesDecoded >= 1 &&
  sample.framesDecoded >= 25 &&
  sample.frameTimes >= 10 &&
  sample.playbackAdvanceSeconds >= 1;

export const collectOfficialMediaQualitySample = async (
  pc: RTCPeerConnection,
  video: HTMLVideoElement
) => {
  const stats = await pc.getStats();
  let videoInbound: any = null;
  let audioInbound: any = null;
  let selectedPair: any = null;
  const byId = new Map<string, any>();
  stats.forEach((value) => {
    byId.set(value.id, value);
    if (value.type === 'inbound-rtp' && value.kind === 'video') videoInbound = value;
    if (value.type === 'inbound-rtp' && value.kind === 'audio') audioInbound = value;
    if (
      value.type === 'candidate-pair' &&
      value.state === 'succeeded' &&
      (value.selected || value.nominated)
    ) {
      selectedPair = value;
    }
  });
  const codec = byId.get(videoInbound?.codecId);
  const localCandidate = byId.get(selectedPair?.localCandidateId);
  const remoteCandidate = byId.get(selectedPair?.remoteCandidateId);
  const display = video.getBoundingClientRect();
  let browserFrameSharpness = 0;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 320;
    canvas.height = 180;
    const context =
      typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)
        ? null
        : canvas.getContext('2d', { willReadFrequently: true });
    if (context && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
      let gradientEnergy = 0;
      let samples = 0;
      const luminanceAt = (x: number, y: number) => {
        const index = (y * canvas.width + x) * 4;
        return (
          pixels[index] * 0.2126 +
          pixels[index + 1] * 0.7152 +
          pixels[index + 2] * 0.0722
        );
      };
      for (let y = 1; y < canvas.height - 1; y += 2) {
        for (let x = 1; x < canvas.width - 1; x += 2) {
          const horizontal = luminanceAt(x + 1, y) - luminanceAt(x - 1, y);
          const vertical = luminanceAt(x, y + 1) - luminanceAt(x, y - 1);
          gradientEnergy += horizontal * horizontal + vertical * vertical;
          samples += 1;
        }
      }
      browserFrameSharpness = samples ? Math.round(gradientEnergy / samples) : 0;
    }
  } catch {
    // Canvas analysis is diagnostic only and must not alter the media path.
  }
  return {
    browserMonotonicMs: Math.round(performance.now()),
    width: Number(videoInbound?.frameWidth || video.videoWidth || 0),
    height: Number(videoInbound?.frameHeight || video.videoHeight || 0),
    framesDecoded: Number(videoInbound?.framesDecoded || 0),
    framesDropped: Number(videoInbound?.framesDropped || 0),
    keyFramesDecoded: Number(videoInbound?.keyFramesDecoded || 0),
    framesPerSecond: Number(videoInbound?.framesPerSecond || 0),
    videoBytesReceived: Number(videoInbound?.bytesReceived || 0),
    packetsReceived: Number(videoInbound?.packetsReceived || 0),
    packetsLost: Number(videoInbound?.packetsLost || 0),
    videoJitterSeconds: Number(videoInbound?.jitter || 0),
    qpSum: Number(videoInbound?.qpSum || 0),
    freezeCount: Number(videoInbound?.freezeCount || 0),
    totalFreezesDurationSeconds: Number(
      videoInbound?.totalFreezesDuration || 0
    ),
    audioBytesReceived: Number(audioInbound?.bytesReceived || 0),
    audioPacketsReceived: Number(audioInbound?.packetsReceived || 0),
    audioPacketsLost: Number(audioInbound?.packetsLost || 0),
    audioJitterSeconds: Number(audioInbound?.jitter || 0),
    audioJitterBufferEmittedCount: Number(
      audioInbound?.jitterBufferEmittedCount || 0
    ),
    audioJitterBufferDelaySeconds: Number(audioInbound?.jitterBufferDelay || 0),
    audioJitterBufferTargetDelaySeconds: Number(
      audioInbound?.jitterBufferTargetDelay || 0
    ),
    audioConcealedSamples: Number(audioInbound?.concealedSamples || 0),
    audioConcealmentEvents: Number(audioInbound?.concealmentEvents || 0),
    audioInsertedSamplesForDeceleration: Number(
      audioInbound?.insertedSamplesForDeceleration || 0
    ),
    audioRemovedSamplesForAcceleration: Number(
      audioInbound?.removedSamplesForAcceleration || 0
    ),
    codec: typeof codec?.mimeType === 'string' ? codec.mimeType : 'unavailable',
    localCandidateType:
      typeof localCandidate?.candidateType === 'string'
        ? localCandidate.candidateType
        : 'unavailable',
    remoteCandidateType:
      typeof remoteCandidate?.candidateType === 'string'
        ? remoteCandidate.candidateType
        : 'unavailable',
    protocol:
      typeof localCandidate?.protocol === 'string'
        ? localCandidate.protocol
        : 'unavailable',
    currentRoundTripTimeSeconds: Number(
      selectedPair?.currentRoundTripTime || 0
    ),
    availableIncomingBitrate: Number(
      selectedPair?.availableIncomingBitrate || 0
    ),
    availableOutgoingBitrate: Number(
      selectedPair?.availableOutgoingBitrate || 0
    ),
    clientWidth: video.clientWidth,
    clientHeight: video.clientHeight,
    displayWidth: Math.round(display.width),
    displayHeight: Math.round(display.height),
    devicePixelRatio: window.devicePixelRatio || 1,
    physicalDisplayWidth: Math.round(display.width * (window.devicePixelRatio || 1)),
    physicalDisplayHeight: Math.round(display.height * (window.devicePixelRatio || 1)),
    browserFrameSharpness,
  };
};

const waitForStableOfficialVideo = async (
  pc: RTCPeerConnection,
  video: HTMLVideoElement,
  hasAudio: () => boolean,
  signal?: AbortSignal,
  timeoutMs = 90_000
) => {
  const startedAt = performance.now();
  let frameCount = 0;
  const frameTimes: number[] = [];
  let firstValidAt: number | null = null;
  let firstMediaTime: number | null = null;
  let stopped = false;
  const countFrame = (now: DOMHighResTimeStamp) => {
    if (stopped) return;
    frameCount += 1;
    frameTimes.push(now);
    while (frameTimes.length > 1 && now - frameTimes[0] > 2_500) {
      frameTimes.shift();
    }
    video.requestVideoFrameCallback?.(countFrame);
  };
  video.requestVideoFrameCallback?.(countFrame);
  try {
    while (performance.now() - startedAt < timeoutMs) {
      throwIfAborted(signal);
      const stats = await pc.getStats();
      let inbound: any = null;
      stats.forEach((value) => {
        if (value.type === 'inbound-rtp' && value.kind === 'video') {
          inbound = value as RTCInboundRtpStreamStats;
        }
      });
      const frameWindowMs =
        frameTimes.length > 1 ? frameTimes.at(-1)! - frameTimes[0] : 0;
      const observedFps =
        frameWindowMs > 0 ? ((frameTimes.length - 1) * 1_000) / frameWindowMs : 0;
      if (
        firstMediaTime === null &&
        video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
      ) {
        firstMediaTime = video.currentTime;
      }
      const valid = officialVideoSampleIsUsable({
        connectionState: pc.connectionState,
        hasAudio: hasAudio(),
        readyState: video.readyState,
        width: video.videoWidth,
        height: video.videoHeight,
        keyFramesDecoded: Number(inbound?.keyFramesDecoded || 0),
        framesDecoded: Number(inbound?.framesDecoded || 0),
        frameTimes: frameTimes.length,
        playbackAdvanceSeconds:
          firstMediaTime === null ? 0 : video.currentTime - firstMediaTime,
      });
      if (valid) {
        firstValidAt ??= performance.now();
        if (performance.now() - firstValidAt >= 2_000) {
          return {
            stableAtMonotonicMs: Math.round(performance.now()),
            frameCount,
            framesDecoded: Number(inbound?.framesDecoded || 0),
            keyFramesDecoded: Number(inbound?.keyFramesDecoded || 0),
            observedFps: Math.round(observedFps * 100) / 100,
          };
        }
      } else {
        firstValidAt = null;
      }
      await delayWithAbort(100, signal);
    }
  } finally {
    stopped = true;
  }
  throw new Error('official_avatar_stable_frame_timeout');
};

const monitorAudibleOnsets = (
  stream: MediaStream,
  audioElement: HTMLAudioElement,
  onAudible: (onset: { browserMonotonicMs: number; rms: number }) => void,
  warmedContext?: AudioContext | null
) => {
  const AudioContextClass = browserAudioContextConstructor();
  if (!warmedContext && !AudioContextClass) return () => undefined;
  const context = warmedContext || new AudioContextClass!();
  const ownsContext = !warmedContext;
  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = 512;
  source.connect(analyser);
  const samples = new Float32Array(analyser.fftSize);
  let inspectionTimer = 0;
  let audible = false;
  let lastAudibleAt = 0;
  const inspect = () => {
    analyser.getFloatTimeDomainData(samples);
    let energy = 0;
    for (const sample of samples) {
      energy += sample * sample;
    }
    const rms = Math.sqrt(energy / samples.length);
    const now = performance.now();
    const outputIsPlaying =
      !audioElement.paused && !audioElement.muted && audioElement.volume > 0;
    if (rms > 0.002 && outputIsPlaying) {
      lastAudibleAt = now;
      if (!audible) {
        audible = true;
        onAudible({ browserMonotonicMs: now, rms });
      }
    } else if (audible && now - lastAudibleAt > 500) {
      audible = false;
    }
  };
  void context.resume().catch(() => undefined);
  inspect();
  inspectionTimer = window.setInterval(inspect, 20);
  return () => {
    window.clearInterval(inspectionTimer);
    source.disconnect();
    analyser.disconnect();
    if (ownsContext) void context.close();
  };
};

const monitorCandidateAudioEnergy = (
  microphoneTrack: MediaStreamTrack,
  callbacks: {
    started: (atMonotonicMs: number, rms: number) => void;
    ended: (
      lastEnergyMonotonicMs: number,
      observedAtMonotonicMs: number,
      peakRms: number
    ) => void;
  },
  warmedContext?: AudioContext | null
) => {
  const AudioContextClass = browserAudioContextConstructor();
  if (!warmedContext && !AudioContextClass) return () => undefined;
  const context = warmedContext || new AudioContextClass!({ latencyHint: 'interactive' });
  const ownsContext = !warmedContext;
  const source = context.createMediaStreamSource(new MediaStream([microphoneTrack]));
  const analyser = context.createAnalyser();
  analyser.fftSize = 512;
  source.connect(analyser);
  const samples = new Uint8Array(analyser.fftSize);
  let active = false;
  let consecutiveEnergySamples = 0;
  let lastEnergyAt = 0;
  let peakRms = 0;
  const inspect = () => {
    analyser.getByteTimeDomainData(samples);
    let energy = 0;
    for (const sample of samples) {
      const centered = (sample - 128) / 128;
      energy += centered * centered;
    }
    const rms = Math.sqrt(energy / samples.length);
    const now = performance.now();
    if (rms > 0.003) {
      consecutiveEnergySamples += 1;
      lastEnergyAt = now;
      peakRms = Math.max(peakRms, rms);
      if (!active && consecutiveEnergySamples >= 2) {
        active = true;
        callbacks.started(now, rms);
      }
      return;
    }
    consecutiveEnergySamples = 0;
    if (active && now - lastEnergyAt >= 120) {
      active = false;
      callbacks.ended(lastEnergyAt, now, peakRms);
      peakRms = 0;
    }
  };
  void context.resume().catch(() => undefined);
  inspect();
  const timer = window.setInterval(inspect, 20);
  return () => {
    window.clearInterval(timer);
    source.disconnect();
    analyser.disconnect();
    if (ownsContext) void context.close();
  };
};

type PeerPacketTimingMonitor = {
  stop: () => void;
  candidateEnergyEnded: (browserMonotonicMs: number) => void;
};

const monitorPeerPacketTimings = (
  pc: RTCPeerConnection,
  report: (type: BrowserTimingEvent, metadata?: Record<string, unknown>) => void,
  jitterBufferTargetMs: number
): PeerPacketTimingMonitor => {
  let stopped = false;
  let timer = 0;
  let lastInboundAudioPackets = 0;
  let lastInboundVideoPackets = 0;
  let lastOutboundAudioPackets = 0;
  let lastInboundAudioAt = 0;
  let lastInboundVideoAt = 0;
  let pendingCandidateEnergyEnd: number | null = null;
  let lastInboundTotalAudioEnergy = 0;
  let lastDecodedEnergyAt = 0;
  let decodedEnergyActive = false;
  let pendingResponseVideoPacket = false;
  let previousJitterEmitted = 0;
  let previousJitterDelay = 0;
  let previousJitterTargetDelay = 0;

  const inspect = async () => {
    if (stopped || pc.connectionState === 'closed') return;
    try {
      const stats = await pc.getStats();
      const now = performance.now();
      let inboundAudio: RTCInboundRtpStreamStats | null = null;
      let inboundVideo: RTCInboundRtpStreamStats | null = null;
      let outboundAudio: RTCOutboundRtpStreamStats | null = null;
      stats.forEach((candidate) => {
        if (candidate.type === 'inbound-rtp' && !candidate.isRemote) {
          if (candidate.kind === 'audio') inboundAudio = candidate;
          if (candidate.kind === 'video') inboundVideo = candidate;
        }
        if (
          candidate.type === 'outbound-rtp' &&
          !candidate.isRemote &&
          candidate.kind === 'audio'
        ) {
          outboundAudio = candidate;
        }
      });

      const inboundAudioPackets = Number(inboundAudio?.packetsReceived || 0);
      if (inboundAudioPackets > lastInboundAudioPackets) {
        lastInboundAudioAt = now;
        lastInboundAudioPackets = inboundAudioPackets;
      }

      const inboundAudioWithEnergy = inboundAudio as
        | (RTCInboundRtpStreamStats & { totalAudioEnergy?: number })
        | null;
      const totalAudioEnergy = Number(inboundAudioWithEnergy?.totalAudioEnergy || 0);
      const decodedEnergyIncreased = totalAudioEnergy > lastInboundTotalAudioEnergy + 1e-9;
      const decodedEnergyJustStarted = decodedEnergyIncreased && !decodedEnergyActive;
      const jitterEmitted = Number(inboundAudio?.jitterBufferEmittedCount || 0);
      if (decodedEnergyIncreased) {
        if (!decodedEnergyActive) {
          decodedEnergyActive = true;
          // The official track sends RTP continuously, including silence. The
          // packet observed immediately before decoder energy rises is the
          // first standards-based browser statistic that can be correlated to
          // response-bearing media without decoding Opus in application code.
          report('browser_audio_RTP_packet_received', {
            browserMonotonicMs: Math.round(lastInboundAudioAt || now),
            decoderEnergyObservedAtMonotonicMs: Math.round(now),
            packetsReceived: inboundAudioPackets,
            packetsLost: Number(inboundAudio?.packetsLost || 0),
            relation: 'nearest_received_packet_before_decoder_energy',
          });
          report('browser_audio_decoder_energy_started', {
            browserMonotonicMs: Math.round(now),
            totalAudioEnergy,
          });
          pendingResponseVideoPacket = true;
        }
        lastDecodedEnergyAt = now;
      } else if (decodedEnergyActive && lastDecodedEnergyAt && now - lastDecodedEnergyAt >= 300) {
        decodedEnergyActive = false;
      }
      lastInboundTotalAudioEnergy = totalAudioEnergy;

      // A single sample at each speech-energy onset is sufficient to
      // correlate receiver jitter with audible playout. Reporting every
      // 20 ms energy increment can saturate the browser's HTTP connection
      // pool on high-RTT relay paths and delay the control requests that
      // return the interview to listening.
      if (decodedEnergyJustStarted && jitterEmitted > previousJitterEmitted) {
        const jitterDelay = Number(inboundAudio?.jitterBufferDelay || 0);
        const jitterTargetDelay = Number(
          (inboundAudio as RTCInboundRtpStreamStats & {
            jitterBufferTargetDelay?: number;
          })?.jitterBufferTargetDelay || 0
        );
        const emittedDelta = jitterEmitted - previousJitterEmitted;
        report('browser_audio_jitter_buffer_emitted', {
          browserMonotonicMs: Math.round(now),
          jitterBufferEmittedCount: jitterEmitted,
          jitterBufferAverageDelayMs: Math.round(
            ((jitterDelay - previousJitterDelay) / emittedDelta) * 1000
          ),
          jitterBufferAverageTargetDelayMs: Math.round(
            ((jitterTargetDelay - previousJitterTargetDelay) / emittedDelta) * 1000
          ),
          jitterBufferRequestedTargetMs: jitterBufferTargetMs,
        });
      }
      previousJitterEmitted = jitterEmitted;
      previousJitterDelay = Number(inboundAudio?.jitterBufferDelay || 0);
      previousJitterTargetDelay = Number(
        (inboundAudio as RTCInboundRtpStreamStats & {
          jitterBufferTargetDelay?: number;
        })?.jitterBufferTargetDelay || 0
      );

      const inboundVideoPackets = Number(inboundVideo?.packetsReceived || 0);
      if (inboundVideoPackets > lastInboundVideoPackets) {
        lastInboundVideoPackets = inboundVideoPackets;
        lastInboundVideoAt = now;
        if (pendingResponseVideoPacket) {
          pendingResponseVideoPacket = false;
          report('browser_video_RTP_packet_received', {
            browserMonotonicMs: Math.round(now),
            packetsReceived: inboundVideoPackets,
            relation: 'first_video_packet_after_audio_response_burst',
          });
        }
      }

      const outboundAudioPackets = Number(outboundAudio?.packetsSent || 0);
      if (
        pendingCandidateEnergyEnd !== null &&
        outboundAudioPackets > lastOutboundAudioPackets
      ) {
        report('candidate_browser_audio_packet_published', {
          browserMonotonicMs: Math.round(now),
          lastMeaningfulEnergyMonotonicMs: Math.round(pendingCandidateEnergyEnd),
          packetsSent: outboundAudioPackets,
        });
        pendingCandidateEnergyEnd = null;
      }
      lastOutboundAudioPackets = outboundAudioPackets;
    } catch {
      // A closing peer may reject getStats; cleanup owns the connection state.
    }
    if (!stopped) timer = window.setTimeout(inspect, 20);
  };
  void inspect();
  return {
    stop: () => {
      stopped = true;
      window.clearTimeout(timer);
    },
    candidateEnergyEnded: (browserMonotonicMs) => {
      pendingCandidateEnergyEnd = browserMonotonicMs;
    },
  };
};

export const attachRemoteAudioStream = async (args: {
  stream: MediaStream;
  audioElement: HTMLAudioElement;
  report: (type: BrowserTimingEvent, metadata?: Record<string, unknown>) => void;
}) => {
  const { stream, audioElement, report } = args;
  report('remote_audio_track_available', {
    browserMonotonicMs: Math.round(performance.now()),
    audioTrackCount: stream.getAudioTracks().length,
  });
  audioElement.srcObject = stream;
  audioElement.muted = false;
  await audioElement.play();
  report('audio_element_playing', {
    browserMonotonicMs: Math.round(performance.now()),
    paused: audioElement.paused,
    muted: audioElement.muted,
    volume: audioElement.volume,
    readyState: audioElement.readyState,
  });
};

export const connectInterviewMedia = async (args: {
  session: OfficialAvatarMediaSession;
  microphoneTrack: MediaStreamTrack;
  cameraTrack?: MediaStreamTrack;
  videoElement: HTMLVideoElement;
  audioElement: HTMLAudioElement;
  callbacks: MediaCallbacks;
  audioOutputWarmup?: InterviewAudioOutputWarmup;
  signal?: AbortSignal;
  isCurrent?: () => boolean;
  isTerminalResponse?: () => boolean;
  onFatalMediaFailure?: (error: Error) => void;
  onCandidateMediaPublished?: () => Promise<void>;
}) => {
  const { session, microphoneTrack, videoElement, audioElement, callbacks } = args;
  const directAudioDiagnostic =
    new URLSearchParams(window.location.search).get(
      'ownedAvatarDirectAudioDiagnostic'
    ) === '1';
  const room = new Room({ adaptiveStream: false, dynacast: false });
  if (directAudioDiagnostic) {
    (
      window as typeof window & {
        __intellerviewOwnedAvatarDirectAudio?: Record<string, unknown>;
      }
    ).__intellerviewOwnedAvatarDirectAudio = { room, track: null, element: null };
    room.on(RoomEvent.TrackSubscribed, (track, publication) => {
      if (
        track.kind !== Track.Kind.Audio ||
        publication.trackName !== 'owned-avatar-diagnostic-direct-audio'
      ) {
        return;
      }
      const element = track.attach() as HTMLAudioElement;
      element.autoplay = true;
      element.muted = false;
      element.dataset.ownedAvatarDiagnostic = 'direct-audio';
      document.body.append(element);
      (
        window as typeof window & {
          __intellerviewOwnedAvatarDirectAudio?: Record<string, unknown>;
        }
      ).__intellerviewOwnedAvatarDirectAudio = { room, track, element };
      void element.play().catch(() => undefined);
    });
  }
  const attemptController = new AbortController();
  const cancelAttempt = () => attemptController.abort();
  args.signal?.addEventListener('abort', cancelAttempt, { once: true });
  throwIfAborted(args.signal);
  const fanOut = fanOutInterviewMicrophone(microphoneTrack);
  const avtrMicrophoneTrack = fanOut.officialListeningTrack;
  const avtrMicrophoneStream = new MediaStream([avtrMicrophoneTrack]);
  const jitterBufferTargetMs = resolveOfficialAvatarJitterBufferTargetMs();
  let pc: RTCPeerConnection | null = null;
  let stopAudibleMonitor: () => void = () => undefined;
  let stopCandidateEnergy: () => void = () => undefined;
  let stopCandidateProgress: () => void = () => undefined;
  let stopMediaQuality: () => void = () => undefined;
  let stopVideoLiveness: () => void = () => undefined;
  let peerPacketTiming: PeerPacketTimingMonitor = {
    stop: () => undefined,
    candidateEnergyEnded: () => undefined,
  };
  let audioPlaybackReadyResolve: () => void = () => undefined;
  let audioPlaybackReadyReject: (error: unknown) => void = () => undefined;
  const audioPlaybackReady = new Promise<void>((resolve, reject) => {
    audioPlaybackReadyResolve = resolve;
    audioPlaybackReadyReject = reject;
  });
  let closed = false;
  let fatalMediaFailureReported = false;
  const reportFatalMediaFailure = (error: Error) => {
    if (closed || fatalMediaFailureReported) return;
    fatalMediaFailureReported = true;
    args.onFatalMediaFailure?.(error);
  };
  const cleanup = async () => {
    if (closed) return;
    closed = true;
    attemptController.abort();
    args.signal?.removeEventListener('abort', cancelAttempt);
    stopAudibleMonitor();
    stopCandidateEnergy();
    stopCandidateProgress();
    stopMediaQuality();
    stopVideoLiveness();
    peerPacketTiming.stop();
    pc?.close();
    avtrMicrophoneTrack.stop();
    audioElement.pause();
    videoElement.srcObject = null;
    audioElement.srcObject = null;
    // The page owns the one captured microphone. Retain it across the single
    // bounded fresh-session recovery and stop it only in page-level cleanup.
    await room.disconnect(false).catch(() => undefined);
  };
  let browserToSessionOffsetMs: number | null = null;
  let browserClockSyncRttMs: number | null = null;
  const reportSafely = (
    type: BrowserTimingEvent,
    metadata?: Record<string, unknown>
  ) => {
    const browserMonotonicMs = metadata?.browserMonotonicMs;
    const aligned =
      browserToSessionOffsetMs !== null && typeof browserMonotonicMs === 'number'
        ? {
            browserSessionRelativeMs: Math.round(
              browserMonotonicMs + browserToSessionOffsetMs
            ),
            browserClockSyncRttMs,
          }
        : {};
    return callbacks.report(type, { ...metadata, ...aligned }).catch(() => undefined);
  };
  try {
    let bestClockSample: { rttMs: number; offsetMs: number } | null = null;
    for (let sample = 0; sample < 5; sample += 1) {
      const sent = performance.now();
      const response = await callbacks.report('browser_clock_sync', {
        browserMonotonicMs: Math.round(sent),
        sampleSequence: sample + 1,
      });
      const received = performance.now();
      const sessionRelativeMs = response.sessionRelativeMs;
      if (typeof sessionRelativeMs !== 'number') continue;
      const rttMs = received - sent;
      const offsetMs = sessionRelativeMs - (sent + received) / 2;
      if (!bestClockSample || rttMs < bestClockSample.rttMs) {
        bestClockSample = { rttMs, offsetMs };
      }
    }
    if (!bestClockSample) throw new Error('owned_avatar_browser_clock_sync_failed');
    browserToSessionOffsetMs = bestClockSample.offsetMs;
    browserClockSyncRttMs = Math.round(bestClockSample.rttMs);
    stopCandidateEnergy = monitorCandidateAudioEnergy(
      microphoneTrack,
      {
        started: (atMonotonicMs, rms) => {
          reportSafely('candidate_browser_audio_started', {
            browserMonotonicMs: Math.round(atMonotonicMs),
            rms,
          });
        },
        ended: (lastEnergyMonotonicMs, observedAtMonotonicMs, peakRms) => {
          peerPacketTiming.candidateEnergyEnded(lastEnergyMonotonicMs);
          reportSafely('candidate_browser_audio_ended', {
            browserMonotonicMs: Math.round(lastEnergyMonotonicMs),
            observedAtMonotonicMs: Math.round(observedAtMonotonicMs),
            detectionLagMs: Math.round(
              observedAtMonotonicMs - lastEnergyMonotonicMs
            ),
            rms: peakRms,
          });
        },
      },
      args.audioOutputWarmup?.context
    );
    if (args.audioOutputWarmup) {
      void args.audioOutputWarmup.result.then((metadata) => {
        reportSafely('browser_audio_output_prewarmed', metadata);
      });
    }
    let audioTrackAttached = false;
    let videoTrackAttached = false;
    let tracksReported = false;
    let appliedJitterTargets: Record<string, number | null> = {};
    const reportTracksAttached = () => {
      if (!audioTrackAttached || !videoTrackAttached || tracksReported) return;
      tracksReported = true;
      void callbacks.report('official_tracks_attached', {
        browserMonotonicMs: Math.round(performance.now()),
        audioTrackCount: 1,
        videoTrackCount: 1,
        audioJitterBufferTargetMs: appliedJitterTargets.audio ?? null,
        videoJitterBufferTargetMs: appliedJitterTargets.video ?? null,
      }).catch(() => undefined);
    };
    const connectLiveKit = async () => {
      reportSafely('LiveKit_signaling_started', {
        browserMonotonicMs: Math.round(performance.now()),
      });
      await room.connect(session.liveKitUrl, session.accessToken, {
        autoSubscribe: directAudioDiagnostic,
      });
      reportSafely('LiveKit_connected', {
        browserMonotonicMs: Math.round(performance.now()),
      });
      const microphonePublication = await room.localParticipant.publishTrack(fanOut.liveKitTrack, {
        source: Track.Source.Microphone,
      });
      const cameraPublication = args.cameraTrack
        ? await room.localParticipant.publishTrack(args.cameraTrack, {
            source: Track.Source.Camera,
          })
        : null;
      return { microphonePublication, cameraPublication };
    };
    const connectOfficial = async () => {
      reportSafely('official_signaling_started', {
        browserMonotonicMs: Math.round(performance.now()),
      });
      let officialAudioConnected = false;
      const negotiation = await negotiateOfficialAvatarPeer({
        microphoneTrack: avtrMicrophoneTrack,
        microphoneStream: avtrMicrophoneStream,
        iceServers: callbacks.iceServers,
        offer: callbacks.offer,
        signal: attemptController.signal,
        isCurrent: args.isCurrent,
        jitterBufferTargetMs,
        onPeerConnection: (candidate) => {
          pc = candidate;
          peerPacketTiming.stop();
          peerPacketTiming = monitorPeerPacketTimings(
            candidate,
            reportSafely,
            jitterBufferTargetMs
          );
          candidate.addEventListener('track', (event) => {
            appliedJitterTargets = applyOfficialAvatarJitterBufferTarget(
              candidate,
              jitterBufferTargetMs
            );
            const stream = event.streams[0] || new MediaStream([event.track]);
            if (event.track.kind === 'video') {
              videoTrackAttached = true;
              videoElement.srcObject = stream;
              void videoElement.play().catch(() => undefined);
              let muteFailureTimer = 0;
              event.track.addEventListener('mute', () => {
                reportSafely('official_video_track_muted', {
                  browserMonotonicMs: Math.round(performance.now()),
                });
                window.clearTimeout(muteFailureTimer);
                muteFailureTimer = window.setTimeout(() => {
                  if (event.track.muted && event.track.readyState === 'live') {
                    reportFatalMediaFailure(
                      new Error('official_avatar_video_track_muted')
                    );
                  }
                }, 2_000);
              });
              event.track.addEventListener('unmute', () => {
                window.clearTimeout(muteFailureTimer);
                reportSafely('official_video_track_resumed', {
                  browserMonotonicMs: Math.round(performance.now()),
                });
              });
              event.track.addEventListener('ended', () => {
                window.clearTimeout(muteFailureTimer);
                reportSafely('official_video_track_ended', {
                  browserMonotonicMs: Math.round(performance.now()),
                });
                if (args.isTerminalResponse?.()) return;
                reportFatalMediaFailure(
                  new Error('official_avatar_video_track_ended')
                );
              });
            } else if (event.track.kind === 'audio') {
              if (audioTrackAttached) return;
              audioTrackAttached = true;
              officialAudioConnected = true;
              stopAudibleMonitor = monitorAudibleOnsets(stream, audioElement, (onset) => {
                const reportVisibleFrame = (
                  visibleAt: number,
                  metadata: Record<string, unknown> = {}
                ) => {
                  void reportSafely('first_audible_browser_audio', {
                    browserMonotonicMs: Math.round(onset.browserMonotonicMs),
                  }).finally(() => {
                    void reportSafely('first_visible_browser_response', {
                      browserMonotonicMs: Math.round(visibleAt),
                      correspondingAudioOnsetMonotonicMs: Math.round(
                        onset.browserMonotonicMs
                      ),
                      ...metadata,
                    });
                  });
                };
                if (typeof videoElement.requestVideoFrameCallback === 'function') {
                  videoElement.requestVideoFrameCallback((now, metadata) => {
                    reportVisibleFrame(now, {
                      mediaTime: metadata.mediaTime,
                      presentedFrames: metadata.presentedFrames,
                    });
                  });
                } else {
                  window.requestAnimationFrame((now) => reportVisibleFrame(now));
                }
              }, args.audioOutputWarmup?.context);
              void attachRemoteAudioStream({
                stream,
                audioElement,
                report: reportSafely,
              }).then(audioPlaybackReadyResolve, audioPlaybackReadyReject);
            }
            reportTracksAttached();
          });
          let connectionReported = false;
          candidate.addEventListener('connectionstatechange', () => {
            if (candidate.connectionState === 'connected' && !connectionReported) {
              connectionReported = true;
              reportSafely('official_WebRTC_connected', {
                browserMonotonicMs: Math.round(performance.now()),
              });
            }
            if (['failed', 'closed', 'disconnected'].includes(candidate.connectionState)) {
              void cleanup();
            }
          });
        },
      });
      pc = negotiation.pc;
      const [stable] = await Promise.all([
        waitForStableOfficialVideo(
          negotiation.pc,
          videoElement,
          () => officialAudioConnected,
          attemptController.signal
        ),
        audioPlaybackReady,
      ]);
      reportSafely('first_stable_visible_frame', stable);
      let lastFrameAt = performance.now();
      let stopped = false;
      let frameCallback = 0;
      const observeFrame = () => {
        if (stopped) return;
        lastFrameAt = performance.now();
        if (typeof videoElement.requestVideoFrameCallback === 'function') {
          frameCallback = videoElement.requestVideoFrameCallback(observeFrame);
        }
      };
      if (typeof videoElement.requestVideoFrameCallback === 'function') {
        frameCallback = videoElement.requestVideoFrameCallback(observeFrame);
      }
      let previousCurrentTime = videoElement.currentTime;
      const livenessTimer = window.setInterval(() => {
        const now = performance.now();
        if (videoElement.currentTime > previousCurrentTime) {
          previousCurrentTime = videoElement.currentTime;
          lastFrameAt = now;
        }
        if (
          negotiation.pc.connectionState === 'connected' &&
          now - lastFrameAt >= 2_000 &&
          !args.isTerminalResponse?.()
        ) {
          reportSafely('official_video_stalled', {
            browserMonotonicMs: Math.round(now),
            lastFrameAgeMs: Math.round(now - lastFrameAt),
          });
          reportFatalMediaFailure(new Error('official_avatar_video_stalled'));
        }
      }, 250);
      stopVideoLiveness = () => {
        stopped = true;
        window.clearInterval(livenessTimer);
        if (
          frameCallback &&
          typeof videoElement.cancelVideoFrameCallback === 'function'
        ) {
          videoElement.cancelVideoFrameCallback(frameCallback);
        }
      };
      return stable;
    };
    room.once(RoomEvent.Disconnected, () => {
      void cleanup();
    });
    const liveKitConnection = args.onCandidateMediaPublished
      ? await (async () => {
          const connected = await connectLiveKit();
          await args.onCandidateMediaPublished?.();
          await connectOfficial();
          return connected;
        })()
      : (await connectMediaTransportsInParallel(connectLiveKit, connectOfficial))[0];
    const liveKitPublication = liveKitConnection.microphonePublication;
    let progressSequence = 0;
    let progressPending = false;
    const reportCandidateProgress = async () => {
      if (closed || progressPending || progressSequence >= 90) return;
      progressPending = true;
      try {
        const liveKitTrack = liveKitPublication.track as
          | { getSenderStats?: () => Promise<Record<string, unknown> | undefined> }
          | undefined;
        const liveKitStats = await liveKitTrack?.getSenderStats?.();
        const officialStats = await pc?.getStats();
        let officialBytesSent = 0;
        let officialPacketsSent = 0;
        officialStats?.forEach((value) => {
          if (value.type === 'outbound-rtp' && value.kind === 'audio') {
            officialBytesSent = Number(value.bytesSent || 0);
            officialPacketsSent = Number(value.packetsSent || 0);
          }
        });
        progressSequence += 1;
        await callbacks.report('candidate_audio_progress', {
          browserMonotonicMs: Math.round(performance.now()),
          sampleSequence: progressSequence,
          liveKitBytesSent: Number(liveKitStats?.bytesSent || 0),
          liveKitPacketsSent: Number(liveKitStats?.packetsSent || 0),
          officialBytesSent,
          officialPacketsSent,
          microphoneEnabled: microphoneTrack.enabled,
          microphoneReadyState: microphoneTrack.readyState,
        });
      } catch {
        // Timing evidence is observational and must never alter media flow.
      } finally {
        progressPending = false;
      }
    };
    const progressTimer = window.setInterval(() => {
      void reportCandidateProgress();
    }, 2_000);
    stopCandidateProgress = () => window.clearInterval(progressTimer);
    void reportCandidateProgress();
    let qualitySequence = 0;
    let previousQuality: {
      atMs: number;
      videoBytesReceived: number;
      framesDecoded: number;
      qpSum: number;
    } | null = null;
    let qualityPending = false;
    const reportMediaQuality = async () => {
      if (closed || qualityPending || qualitySequence >= 30 || !pc) return;
      qualityPending = true;
      try {
        const sample = await collectOfficialMediaQualitySample(pc, videoElement);
        qualitySequence += 1;
        const elapsedMs = previousQuality
          ? sample.browserMonotonicMs - previousQuality.atMs
          : 0;
        const videoBitrateBps =
          previousQuality && elapsedMs > 0
            ? Math.max(
                0,
                Math.round(
                  ((sample.videoBytesReceived -
                    previousQuality.videoBytesReceived) *
                    8_000) /
                    elapsedMs
                )
              )
            : 0;
        const priorQuality = previousQuality;
        previousQuality = {
          atMs: sample.browserMonotonicMs,
          videoBytesReceived: sample.videoBytesReceived,
          framesDecoded: sample.framesDecoded,
          qpSum: sample.qpSum,
        };
        const decodedDelta = priorQuality
          ? sample.framesDecoded - priorQuality.framesDecoded
          : 0;
        const averageQp =
          priorQuality && decodedDelta > 0
            ? Math.round(
                ((sample.qpSum - priorQuality.qpSum) / decodedDelta) * 100
              ) / 100
            : 0;
        await callbacks.report('browser_media_quality_sample', {
          ...sample,
          sampleSequence: qualitySequence,
          videoBitrateBps,
          averageQp,
        });
      } catch {
        // Quality telemetry is observational and must never alter media flow.
      } finally {
        qualityPending = false;
      }
    };
    const qualityTimer = window.setInterval(() => {
      void reportMediaQuality();
    }, 5_000);
    stopMediaQuality = () => window.clearInterval(qualityTimer);
    void reportMediaQuality();
    return {
      room,
      peerConnection: pc,
      liveKitMicrophoneTrack: fanOut.liveKitTrack,
      officialMicrophoneTrack: avtrMicrophoneTrack,
      cleanup,
    };
  } catch (error) {
    await cleanup();
    throw error;
  }
};
